import {
  Alert,
  AlertDescription,
  AlertIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Collapse,
  Divider,
  Flex,
  FormLabel,
  Grid,
  GridItem,
  IconButton,
  Input,
  SimpleGrid,
  Skeleton,
  SkeletonText,
  Switch,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack
} from '@chakra-ui/react'
import { QRCodeCanvas } from 'qrcode.react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { ActionButton } from '../../../components/ActionButton'
import { BackLink } from '../../../components/BackLink'
import { ConfirmDialog } from '../../../components/ConfirmDialog'
import { EmptyCell } from '../../../components/EmptyCell'
import { EmptyState } from '../../../components/EmptyState'
import { FormField } from '../../../components/FormField'
import { FormModal } from '../../../components/FormModal'
import { Icon } from '../../../components/Icon'
import { MoneyInput } from '../../../components/MaskedInput'
import { PageTitle } from '../../../components/PageTitle'
import PayoutModal from '../../../components/PayoutModal'
import PostAuctionSaleModal from '../../../components/PostAuctionSaleModal'
import ArtworkPreviewModal from '../../../components/previewModal/ArtworkPreviewModal'
import { SectionCard, StatCard } from '../../../components/SectionCard'
import { FormSelect } from '../../../components/Select'
import ShortText from '../../../components/ShortText'
import { StatusBadge } from '../../../components/StatusBadge'
import { TableEmptyRow } from '../../../components/TableEmptyRow'
import { Tooltip } from '../../../components/Tooltip'
import {
  formatCurrency,
  formatCurrencyValue,
  formatDateTime,
  formatLocalDateTime,
  formatPhone,
  useAppToast,
  useDownload,
  useHasRole
} from '../../../helpers'
import { getArtworkSaleStatus } from '../../../helpers/artworkSaleStatus'
import { useEventWebSocket } from '../../../hooks/useEventWebSocket'
import ContestsService from '../../../services/Contests/ContestsService'
import { EventAdminService } from '../../../services/Event/EventAdminService'
import { EventMachineService } from '../../../services/Event/EventMachineService'
import { EventService } from '../../../services/Event/EventService'
import {
  AttendeesCheckinResponse,
  CaucaoParticipantsResponse,
  EventAttendeeCheckin,
  EventDetail,
  EventMachine,
  LiveArtwork,
  LiveBid,
  LiveCheckin,
  LiveStats
} from '../../../services/Event/types'
import AuctionWhatsAppInfoModal from './AuctionWhatsAppInfoModal'
import CheckoutPaymentModal from './checkout/CheckoutPaymentModal'

/** Incremento mínimo de lance, em reais. */
const MIN_BID_INCREMENT = 1

/**
 * Coerção explícita de um boolean vindo do WS: `data` é Record<string, unknown> (o `as boolean`
 * não coage em runtime). Garante boolean real mesmo se o backend mandar 1/0 ou string
 * ("true"/"1"), evitando truthy que acende o selo errado.
 */
const wsBool = (value: unknown): boolean => value === true || value === 1 || value === '1' || value === 'true'

export default function EventAdminPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const toast = useAppToast()

  const eventService = new EventService()
  const eventMachineService = new EventMachineService()

  const [event, setEvent] = useState<EventDetail | null>(null)
  const [loading, setLoading] = useState(true)

  const hasContest = event?.contests && event.contests.length > 0
  // The tab also owes the establishments' sales (ABRA-425): an event without a contest has a tab too.
  const showComanda = hasContest || (event?.establishments_count ?? 0) > 0
  // Cartões de indicadores: os dois de check-in mais os dois infantis; com concurso, mais os dois do
  // leilão. Contagem e colunas saem do mesmo par para o esqueleto e a grade nunca divergirem.
  const statCardCount = hasContest ? 6 : 4
  const statColumns = { base: 2, md: hasContest ? 3 : 4, xl: hasContest ? 6 : 4 }

  const [machines, setMachines] = useState<EventMachine[]>([])
  const [loadingMachines, setLoadingMachines] = useState(false)
  const [machinesError, setMachinesError] = useState(false)
  const [showDevices, setShowDevices] = useState(false)

  const [activationCode, setActivationCode] = useState('')
  const [activationExpiresAt, setActivationExpiresAt] = useState('')
  const [qrModalOpen, setQrModalOpen] = useState(false)
  const [generatingCode, setGeneratingCode] = useState(false)

  const [nameModalOpen, setNameModalOpen] = useState(false)
  const [deviceName, setDeviceName] = useState('')
  const [generateCodeError, setGenerateCodeError] = useState<string | null>(null)

  const [deviceStatusFilter, setDeviceStatusFilter] = useState('active')

  const [deactivateTarget, setDeactivateTarget] = useState<EventMachine | null>(null)
  const [confirmDeactivateOpen, setConfirmDeactivateOpen] = useState(false)

  const [liveStats, setLiveStats] = useState<LiveStats | null>(null)
  const [recentCheckins, setRecentCheckins] = useState<LiveCheckin[]>([])
  const [artworks, setArtworks] = useState<LiveArtwork[]>([])
  const [recentBids, setRecentBids] = useState<LiveBid[]>([])
  const [liveLoading, setLiveLoading] = useState(true)
  const [liveError, setLiveError] = useState(false)

  const [auctionEnded, setAuctionEnded] = useState(false)
  const [auctionToggleLoading, setAuctionToggleLoading] = useState(false)
  const [auctionConfirmOpen, setAuctionConfirmOpen] = useState(false)

  const [payoutModalArtId, setPayoutModalArtId] = useState<number | null>(null)
  const [postAuctionArt, setPostAuctionArt] = useState<LiveArtwork | null>(null)
  const [saleStatusFilter, setSaleStatusFilter] = useState<'available' | 'sold' | 'unsold' | ''>('available')
  const [artworkSearch, setArtworkSearch] = useState('')
  const { isDownloading: isExportingRepasse, download: exportRepasse } = useDownload()
  const [selectedArtwork, setSelectedArtwork] = useState<LiveArtwork | null>(null)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const contestsService = new ContestsService()

  const [attendees, setAttendees] = useState<EventAttendeeCheckin[]>([])
  const [attendeesLoading, setAttendeesLoading] = useState(false)
  // A reload of the same page keeps the table on screen; swapping it for the skeleton
  // shortens the page and the browser scrolls up.
  const [attendeesRefreshing, setAttendeesRefreshing] = useState(false)
  const attendeesReplacing = attendeesLoading && !attendeesRefreshing
  const [attendeesError, setAttendeesError] = useState(false)
  const [attendeesTotal, setAttendeesTotal] = useState(0)
  const [attendeesPage, setAttendeesPage] = useState(1)
  const [attendeesSearch, setAttendeesSearch] = useState('')
  const [attendeesFilter, setAttendeesFilter] = useState<'all' | 'no_checkin' | 'no_checkout'>('all')
  const [checkinAction, setCheckinAction] = useState<{
    type: 'checkin' | 'checkout'
    attendee: EventAttendeeCheckin
  } | null>(null)
  const [checkinActionLoading, setCheckinActionLoading] = useState(false)
  // ABRA-425 — whoever still owes on the tab leaves through the check-out with payment.
  const [paymentCheckoutAttendee, setPaymentCheckoutAttendee] = useState<EventAttendeeCheckin | null>(null)
  const [auctionEndsAt, setAuctionEndsAt] = useState<string | null>(null)
  const [batchRefundLoading, setBatchRefundLoading] = useState(false)
  const [batchRefundConfirmOpen, setBatchRefundConfirmOpen] = useState(false)
  const [caucaoModalOpen, setCaucaoModalOpen] = useState(false)
  const [caucaoData, setCaucaoData] = useState<CaucaoParticipantsResponse | null>(null)
  const [caucaoLoading, setCaucaoLoading] = useState(false)
  const [caucaoError, setCaucaoError] = useState(false)
  const [reconcileLoading, setReconcileLoading] = useState(false)
  const [bidIncrement, setBidIncrement] = useState<number | null>(50)
  const [whatsappNotifications, setWhatsappNotifications] = useState<boolean>(false)
  const [whatsappInfoOpen, setWhatsappInfoOpen] = useState(false)
  const [savingWhatsapp, setSavingWhatsapp] = useState(false)
  const [savingAuctionConfig, setSavingAuctionConfig] = useState(false)

  const artworksRef = useRef<LiveArtwork[]>([])
  const attendeesRequestIdRef = useRef(0)
  artworksRef.current = artworks

  const hasRole = useHasRole()
  const permissions = {
    manageEventCheckin: hasRole('manage_event_checkin')
  }

  useEffect(() => {
    if (event?.contests?.[0]) {
      setAuctionEndsAt(event.contests[0].auction_ends_at ?? null)
      setBidIncrement(Number(event.contests[0].bid_increment ?? 50))
      setWhatsappNotifications(Boolean(event.contests[0].auction_whatsapp_notifications))
    }
  }, [event])

  const filteredArtworks = (() => {
    let result = artworks
    if (saleStatusFilter) {
      result = result.filter((a) => {
        if (saleStatusFilter === 'available') return a.sale_status === 'available' && a.available_for_auction
        if (saleStatusFilter === 'unsold')
          return a.sale_status === 'unsold' || (!a.available_for_auction && a.sale_status !== 'sold')
        return a.sale_status === saleStatusFilter
      })
    }
    if (artworkSearch.trim()) {
      const search = artworkSearch.trim().toLowerCase()
      result = result.filter(
        (a) => a.name?.toLowerCase().includes(search) || a.author_name?.toLowerCase().includes(search)
      )
    }
    return result
  })()

  useEffect(() => {
    eventService
      .getBySlug(slug!)
      .then((data) => setEvent(data))
      .catch(() => {
        toast.error('Não foi possível carregar o evento')
        navigate('/events')
      })
      .finally(() => setLoading(false))
  }, [slug])

  const loadMachines = useCallback(() => {
    if (!event) return
    setLoadingMachines(true)
    setMachinesError(false)
    eventMachineService
      .listMachines(event.id)
      .then((data) => setMachines(data))
      .catch((error) => {
        setMachinesError(true)
        toast.error('Erro ao carregar os dispositivos', error)
      })
      .finally(() => setLoadingMachines(false))
  }, [event])

  useEffect(() => {
    if (event) loadMachines()
  }, [event])

  const loadLiveData = useCallback(() => {
    if (!event) return
    const adminService = new EventAdminService()
    adminService
      .getLiveData(event.id)
      .then((data) => {
        setLiveStats(data.stats)
        setRecentCheckins(data.recent_checkins)
        setArtworks(data.artworks)
        setRecentBids(data.recent_bids)
        setAuctionEnded(data.auction_ended ?? false)
      })
      .catch((error) => {
        // O WebSocket ainda pode trazer atualizações depois, mas enquanto não vier a tela não
        // pode afirmar que não há check-in nem lance nenhum: as três listas passam a mostrar a
        // falha em vez do vazio.
        setLiveError(true)
        toast.error('Erro ao carregar os dados ao vivo', error)
      })
      .finally(() => setLiveLoading(false))
  }, [event]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (event) loadLiveData()
  }, [event]) // eslint-disable-line react-hooks/exhaustive-deps

  const loadAttendees = useCallback(
    (page: number, search: string, filter: 'all' | 'no_checkin' | 'no_checkout', keepRows = false) => {
      if (!event) return

      const currentRequestId = ++attendeesRequestIdRef.current
      setAttendeesLoading(true)
      setAttendeesRefreshing(keepRows)
      setAttendeesError(false)

      eventService
        .getAttendeesCheckin(event.id, {
          page,
          page_size: 20,
          search: search || undefined,
          no_checkin: filter === 'no_checkin' || undefined,
          no_checkout: filter === 'no_checkout' || undefined
        })
        .then((res: AttendeesCheckinResponse) => {
          // Only update if this is the latest request
          if (currentRequestId === attendeesRequestIdRef.current) {
            setAttendees(res.data)
            setAttendeesTotal(res.total)
          }
        })
        .catch((error) => {
          // Only show error if this is the latest request
          if (currentRequestId === attendeesRequestIdRef.current) {
            setAttendeesError(true)
            toast.error('Erro ao carregar os inscritos', error)
          }
        })
        .finally(() => {
          // Only update loading if this is the latest request
          if (currentRequestId === attendeesRequestIdRef.current) {
            setAttendeesLoading(false)
            setAttendeesRefreshing(false)
          }
        })
    },
    [event, toast] // eslint-disable-line react-hooks/exhaustive-deps
  )

  useEffect(() => {
    if (event) loadAttendees(1, '', 'all')
  }, [event]) // eslint-disable-line react-hooks/exhaustive-deps

  const loadCaucaoParticipants = useCallback(async () => {
    if (!event) return
    setCaucaoLoading(true)
    setCaucaoError(false)
    try {
      const res = await eventService.getCaucaoParticipants(event.id)
      setCaucaoData(res)
    } catch (error) {
      setCaucaoError(true)
      toast.error('Erro ao carregar os participantes com caução', error)
    } finally {
      setCaucaoLoading(false)
    }
  }, [event, toast])

  const handleOpenCaucaoModal = () => {
    setCaucaoModalOpen(true)
    loadCaucaoParticipants()
  }

  const handleBatchRefund = async () => {
    if (!event) return
    setBatchRefundLoading(true)
    try {
      const res = await eventService.refundCheckedInFreeTickets(event.id)
      if (res.refunded_count > 0) {
        toast.success('Reembolso solicitado', res.message)
      } else {
        toast.info('Nenhum reembolso', res.message)
      }
      if (res.refunded_count > 0) {
        loadAttendees(attendeesPage, attendeesSearch, attendeesFilter, true)
        loadCaucaoParticipants()
      }
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Não foi possível processar os reembolsos.'
      toast.error(message)
      // Refresh so a freshly-blocked window (release date) reflects in the UI.
      loadCaucaoParticipants()
    } finally {
      setBatchRefundLoading(false)
      setBatchRefundConfirmOpen(false)
    }
  }

  const handleReconcileCaucao = async () => {
    if (!event) return
    setReconcileLoading(true)
    try {
      const res = await eventService.reconcileCaucaoRefunds(event.id)
      if (res.reconciled_count > 0) {
        toast.success('Status atualizado', res.message)
      } else {
        toast.info('Nada a atualizar', res.message)
      }
      loadCaucaoParticipants()
      if (res.reconciled_count > 0) {
        loadAttendees(attendeesPage, attendeesSearch, attendeesFilter, true)
      }
    } catch {
      toast.error('Não foi possível reverificar o status dos reembolsos')
    } finally {
      setReconcileLoading(false)
    }
  }

  // Debounce attendees search: only trigger fetch after 400ms of user inactivity
  useEffect(() => {
    const timer = setTimeout(() => {
      loadAttendees(1, attendeesSearch, attendeesFilter)
    }, 400)
    return () => clearTimeout(timer)
  }, [attendeesSearch, attendeesFilter, loadAttendees])

  // Determine if event is currently ongoing (open for check-in)
  // A tick counter is used to trigger a re-render at event start/end boundaries
  // so the action column stays accurate without a manual page refresh.
  const [eventBoundaryTick, setEventBoundaryTick] = useState(0)

  useEffect(() => {
    if (!event) return

    const now = new Date()
    const startDate = event.date_start.split('T')[0]
    const endDate = event.date_end ? event.date_end.split('T')[0] : startDate
    const startTime = event.time_start ?? '00:00'
    const endTime = event.time_end ?? '23:59'
    const start = new Date(`${startDate}T${startTime}:00`)
    const end = new Date(`${endDate}T${endTime}:00`)

    const nextBoundaryMs =
      now < start ? start.getTime() - now.getTime() : now <= end ? end.getTime() - now.getTime() : null

    if (nextBoundaryMs === null || nextBoundaryMs <= 0) return

    const timer = setTimeout(() => setEventBoundaryTick((t) => t + 1), nextBoundaryMs + 500)
    return () => clearTimeout(timer)
    // eventBoundaryTick in deps ensures the effect re-schedules for the next
    // boundary after each one fires (start → then end, etc.)
  }, [event, eventBoundaryTick])

  // Determine available action for each attendee based on SSD rules
  const getAvailableAction = (attendee: EventAttendeeCheckin): 'checkin' | 'checkout' | null => {
    const { last_checkin_at, last_checkout_at } = attendee

    // A child check-in has no card, so the terminal cannot close it: the panel is the only way
    // out, and the API records it as 'admin_closed'. Only `is_child` (the active check-in) says
    // that: a child ticket converted to a card is a regular check-in.
    if (attendee.is_child) {
      const active = !!last_checkin_at && (!last_checkout_at || new Date(last_checkin_at) > new Date(last_checkout_at))
      return active ? 'checkout' : 'checkin'
    }

    if (!last_checkin_at && !last_checkout_at) {
      return 'checkin'
    }

    if (last_checkin_at && !last_checkout_at) {
      // A tab that still owes checks out too, with the payment (ABRA-425).
      return 'checkout'
    }

    if (last_checkin_at && last_checkout_at) {
      if (new Date(last_checkin_at) > new Date(last_checkout_at)) {
        return 'checkout'
      }
      return 'checkin'
    }

    return null
  }

  // Handle manual check-in / check-out confirmation
  const handleCheckinAction = async () => {
    if (checkinActionLoading) return
    if (!checkinAction || !event) return

    setCheckinActionLoading(true)
    try {
      if (checkinAction.type === 'checkin') {
        await eventService.manualCheckin(event.id, checkinAction.attendee.id)
      } else {
        await eventService.manualCheckout(event.id, checkinAction.attendee.id)
      }

      toast.success(
        checkinAction.type === 'checkin' ? 'Check-in realizado' : 'Check-out realizado',
        `${checkinAction.attendee.name}`
      )

      loadAttendees(attendeesPage, attendeesSearch, attendeesFilter, true)
    } catch (err: unknown) {
      toast.error(
        checkinAction.type === 'checkin' ? 'Erro ao realizar o check-in' : 'Erro ao realizar o check-out',
        err
      )
    } finally {
      setCheckinActionLoading(false)
      setCheckinAction(null)
    }
  }

  // Auto-adjust saleStatusFilter based on auction status
  useEffect(() => {
    if (!liveLoading) {
      setSaleStatusFilter(auctionEnded ? 'sold' : 'available')
    }
  }, [auctionEnded, liveLoading])

  const handleWsEvent = useCallback((wsEvent: { event: string; data: Record<string, unknown> }) => {
    const { event: eventType, data } = wsEvent

    if (eventType === 'checkin') {
      const isChild = wsBool(data.is_child)
      // The API sends the fresh child count: a child coming back is not a new one.
      const childCount = typeof data.child_checkins === 'number' ? data.child_checkins : null
      setRecentCheckins((prev) => {
        const newCheckin: LiveCheckin = {
          id: (data.id as number) || Date.now(),
          participant_name: data.participant_name as string,
          tab_code: data.tab_code as string,
          checkin_at: data.checkin_at as string,
          checkout_at: null,
          is_child: isChild
        }
        return [newCheckin, ...prev].slice(0, 20)
      })
      setLiveStats((prev) =>
        prev
          ? {
              ...prev,
              total_checkins: prev.total_checkins + 1,
              active_checkins: prev.active_checkins + 1,
              // Without the count (an older API), add one; `?? 0` keeps the state from turning NaN.
              child_checkins: childCount ?? (prev.child_checkins ?? 0) + (isChild ? 1 : 0)
            }
          : prev
      )
    }

    if (eventType === 'checkout') {
      // Só reconcilia por tab_code quando ele é uma string não-vazia, evitando
      // casar entradas com tab_code ausente/indefinido (undefined === undefined).
      const tabCode = typeof data.tab_code === 'string' && data.tab_code ? data.tab_code : null
      if (tabCode) {
        setRecentCheckins((prev) =>
          prev.map((c) =>
            c.tab_code === tabCode && !c.checkout_at ? { ...c, checkout_at: data.checkout_at as string } : c
          )
        )
      }
      // A cancelled child check-in sends the fresh child count: the child may still count through
      // an earlier check-in. Converted to a card or closed by the admin, the child still counts.
      // Without the count (an older API), a cancellation subtracts one.
      const childCount = typeof data.child_checkins === 'number' ? data.child_checkins : null
      const childCancelled = wsBool(data.is_child) && data.close_reason === 'cancelled'
      setLiveStats((prev) =>
        prev
          ? {
              ...prev,
              active_checkins: Math.max(0, prev.active_checkins - 1),
              child_checkins:
                childCount ?? (childCancelled ? Math.max(0, (prev.child_checkins ?? 0) - 1) : prev.child_checkins)
            }
          : prev
      )
    }

    if (eventType === 'bid') {
      const artId = data.art_id as number
      const bidAmount = String(data.bid_amount)
      const tabCode = data.tab_code as string
      const participantName = data.participant_name as string | null

      setArtworks((prev) =>
        prev
          .map((a) =>
            a.id === artId
              ? {
                  ...a,
                  current_bid: bidAmount,
                  current_bid_tab_code: tabCode,
                  current_bid_participant_name: participantName
                }
              : a
          )
          .sort((a, b) => parseFloat(b.current_bid || '0') - parseFloat(a.current_bid || '0'))
      )

      setRecentBids((prev) => {
        const newBid: LiveBid = {
          id: (data.id as number) || Date.now(),
          art_name: data.art_name as string,
          bid_amount: bidAmount,
          tab_code: tabCode,
          participant_name: participantName,
          created_at: new Date().toISOString()
        }
        return [newBid, ...prev].slice(0, 20)
      })

      setLiveStats((prev) => {
        if (!prev) return prev
        const totalValue = artworksRef.current.reduce(
          (sum: number, a: LiveArtwork) => sum + parseFloat(a.current_bid || '0'),
          0
        )
        return { ...prev, total_bids: prev.total_bids + 1, total_auction_value: totalValue.toFixed(2) }
      })
    }
  }, [])

  const { isConnected } = useEventWebSocket({ eventId: event?.id || null, onEvent: handleWsEvent })

  const handleOpenNameModal = () => {
    setDeviceName('')
    setGenerateCodeError(null)
    setNameModalOpen(true)
  }

  const handleGenerateCode = () => {
    if (!event || !deviceName.trim()) return
    setGeneratingCode(true)
    setGenerateCodeError(null)
    eventMachineService
      .generateActivationCode(event.id, deviceName.trim())
      .then((data) => {
        setNameModalOpen(false)
        setActivationCode(data.code)
        setActivationExpiresAt(data.expires_at)
        setQrModalOpen(true)
      })
      .catch((err) => {
        const fieldErrors = err?.response?.data?.errors
        const message =
          (fieldErrors && (Object.values(fieldErrors).flat() as string[])[0]) ||
          err?.response?.data?.message ||
          'Não foi possível gerar o código.'
        setGenerateCodeError(message)
      })
      .finally(() => setGeneratingCode(false))
  }

  const handleCloseQrModal = () => {
    setQrModalOpen(false)
    setActivationCode('')
    setActivationExpiresAt('')
    loadMachines()
  }

  const handleDeactivateClick = (machine: EventMachine) => {
    setDeactivateTarget(machine)
    setConfirmDeactivateOpen(true)
  }

  const handleConfirmDeactivate = () => {
    if (!event || !deactivateTarget) return
    eventMachineService
      .deactivateMachine(event.id, deactivateTarget.id)
      .then(() => {
        toast.success('Dispositivo desconectado')
        loadMachines()
      })
      .catch(() => {
        toast.error('Não foi possível desativar o dispositivo')
      })
      .finally(() => {
        setConfirmDeactivateOpen(false)
        setDeactivateTarget(null)
      })
  }

  const handleToggleAuction = async () => {
    setAuctionToggleLoading(true)
    try {
      const res = await eventMachineService.toggleAuction(event!.id)
      setAuctionEnded(res.action === 'close')
      if (res.action === 'close') {
        toast.warning('Leilão encerrado', 'O leilão será encerrado em 1 minuto.')
      } else {
        toast.success('Leilão reaberto', 'Os tablets podem registrar novos lances.')
      }
    } catch (e) {
      toast.error('Erro ao alterar status do leilão', e)
    } finally {
      setAuctionToggleLoading(false)
      setAuctionConfirmOpen(false)
    }
  }

  const aedFormatted = (() => {
    if (!auctionEndsAt) return ''
    const date = new Date(auctionEndsAt)
    if (isNaN(date.getTime())) return ''
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(
      date.getMinutes()
    )}`
  })()

  /**
   * O leilão precisa de um incremento de verdade: com zero, todo lance empata com o anterior.
   *
   * O campo antigo declarava `min={1}` num `type="number"`, mas não há `<form>` nesta tela e o
   * salvar é um `onClick` — então o atributo só limitava as setinhas do spinner e o valor zero
   * sempre pôde ser salvo. A regra passa a existir de fato.
   */
  const bidIncrementError =
    bidIncrement == null || bidIncrement < MIN_BID_INCREMENT
      ? `O incremento mínimo é ${formatCurrency(MIN_BID_INCREMENT)}.`
      : undefined

  const handleSaveAuctionConfig = async () => {
    if (!event || !event.contests?.[0]) return
    if (bidIncrementError) {
      toast.warning(bidIncrementError)
      return
    }
    const contest = event.contests[0]
    const saved = {
      auction_ends_at: auctionEndsAt || null,
      bid_increment: bidIncrement
    }
    setSavingAuctionConfig(true)
    try {
      await contestsService.update(contest.id, saved)
      // Sem isto o evento em memória segue com os valores antigos, e qualquer
      // releitura dele (ou um erro no próximo salvamento) reverte a tela para
      // o que estava antes.
      setEvent((current) =>
        current
          ? {
              ...current,
              contests: current.contests.map((c) => (c.id === contest.id ? { ...c, ...saved } : c))
            }
          : current
      )
      toast.success('Configurações salvas')
    } catch (e) {
      // Devolve os campos ao que está no servidor: deixá-los exibindo o valor
      // que falhou ao salvar faz o operador acreditar que salvou.
      setAuctionEndsAt(contest.auction_ends_at ?? null)
      setBidIncrement(Number(contest.bid_increment ?? 50))
      toast.error('Erro ao salvar configurações do leilão', e)
    } finally {
      setSavingAuctionConfig(false)
    }
  }

  /**
   * O switch salva sozinho, diferente dos dois campos acima: um switch que só
   * aplica depois de um clique em Salvar é lido como já aplicado, e a pessoa sai
   * da tela achando que ligou os avisos.
   */
  const handleToggleWhatsappNotifications = async (next: boolean) => {
    if (!event || !event.contests?.[0]) return
    const contest = event.contests[0]

    // Optimistic update — revert if the request fails.
    setWhatsappNotifications(next)
    setSavingWhatsapp(true)

    try {
      await contestsService.update(contest.id, { auction_whatsapp_notifications: next })
      setEvent((current) =>
        current
          ? {
              ...current,
              contests: current.contests.map((c) =>
                c.id === contest.id ? { ...c, auction_whatsapp_notifications: next } : c
              )
            }
          : current
      )
      toast.success(next ? 'Avisos por WhatsApp ligados neste leilão.' : 'Avisos por WhatsApp desligados neste leilão.')
    } catch (e) {
      setWhatsappNotifications(!next)
      toast.error('Erro ao atualizar os avisos por WhatsApp', e)
    } finally {
      setSavingWhatsapp(false)
    }
  }

  const handleOpenPreview = (artwork: LiveArtwork) => {
    setSelectedArtwork(artwork)
    setIsPreviewOpen(true)
  }

  const handleClosePreview = () => {
    setIsPreviewOpen(false)
    setSelectedArtwork(null)
  }

  const handlePrevArtwork = () => {
    if (!selectedArtwork) return
    const currentIndex = filteredArtworks.findIndex((a) => a.id === selectedArtwork.id)
    if (currentIndex > 0) {
      setSelectedArtwork(filteredArtworks[currentIndex - 1])
    }
  }

  const handleNextArtwork = () => {
    if (!selectedArtwork) return
    const currentIndex = filteredArtworks.findIndex((a) => a.id === selectedArtwork.id)
    if (currentIndex < filteredArtworks.length - 1) {
      setSelectedArtwork(filteredArtworks[currentIndex + 1])
    }
  }

  const isMachineActive = (m: EventMachine) =>
    m.status === 'active' && (!m.session_expires_at || new Date(m.session_expires_at) > new Date())

  const activeDevices = machines.filter(isMachineActive).length

  const filteredDevices = deviceStatusFilter
    ? machines.filter((m) => (deviceStatusFilter === 'active' ? isMachineActive(m) : !isMachineActive(m)))
    : machines

  if (loading) {
    return (
      <>
        <Skeleton height="16px" width="150px" mb={5} borderRadius="md" />
        <Skeleton height="36px" width="350px" mb={2} borderRadius="md" />
        <Skeleton height="16px" width="200px" mb={8} borderRadius="md" />
        <SimpleGrid columns={4} spacing={4} mb={6}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} height="90px" borderRadius="xl" />
          ))}
        </SimpleGrid>
        <SimpleGrid columns={2} spacing={4}>
          <Skeleton height="300px" borderRadius="xl" />
          <Skeleton height="300px" borderRadius="xl" />
        </SimpleGrid>
      </>
    )
  }

  if (!event) return null

  return (
    <>
      {/* Header */}
      <BackLink to="/events" mb={5}>
        Voltar para Eventos
      </BackLink>

      <PageTitle
        title={event.name}
        description="Painel do Evento"
        uppercase={false}
        mb={6}
        actions={
          <>
            <Flex
              align="center"
              gap={2}
              bg={isConnected ? 'green.50' : 'red.50'}
              px={3}
              py={1.5}
              borderRadius="full"
              border="1px"
              borderColor={isConnected ? 'green.200' : 'red.200'}
            >
              <Box
                w="8px"
                h="8px"
                borderRadius="full"
                bg={isConnected ? 'green.400' : 'red.400'}
                sx={
                  isConnected
                    ? {
                        animation: 'pulse 2s infinite',
                        '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.4 } }
                      }
                    : {}
                }
              />
              <Text fontSize="xs" fontWeight="600" color={isConnected ? 'green.700' : 'red.700'}>
                {isConnected ? 'Ao vivo' : 'Reconectando...'}
              </Text>
            </Flex>
            <Button size="sm" variant="outline" onClick={() => navigate(`/events/${slug}/transactions`)}>
              <Icon name="lni lni-credit-cards" mr="6px" />
              Pagamentos no Local
            </Button>
            <Button size="sm" variant="outline" onClick={() => navigate(`/events/${slug}/tabs`)}>
              <Icon name="lni lni-list" mr="6px" />
              Comandas
            </Button>
            {hasContest && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => window.open(`/events/${slug}/auction-screen`, '_blank', 'noopener,noreferrer')}
              >
                <Icon name="lni lni-display-alt" mr="6px" />
                Tela do Leilão
              </Button>
            )}
            {hasContest && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowDevices(!showDevices)}
                rightIcon={<Icon name={showDevices ? 'lni lni-chevron-up' : 'lni lni-chevron-down'} />}
              >
                <Icon name="lni lni-cog" mr="6px" />
                Dispositivos ({activeDevices}/{machines.length})
              </Button>
            )}
          </>
        }
      />

      {/* Collapsible device management */}
      {hasContest && (
        <Collapse in={showDevices} animateOpacity>
          <SectionCard
            title="Gerenciamento de Dispositivos"
            mb={6}
            actions={
              <Button colorScheme="magenta" size="sm" onClick={handleOpenNameModal} isLoading={generatingCode}>
                <Icon name="lni lni-plus" mr="6px" />
                Ativar Novo Dispositivo
              </Button>
            }
          >
            <Flex mb={4}>
              <FormField label="Status" maxW="160px">
                <FormSelect
                  size="sm"
                  value={deviceStatusFilter}
                  onChange={(value) => setDeviceStatusFilter(value)}
                  options={[
                    { value: 'active', label: 'Ativo' },
                    { value: 'inactive', label: 'Inativo' }
                  ]}
                  placeholder="Todos"
                />
              </FormField>
            </Flex>

            {loadingMachines ? (
              <SkeletonText noOfLines={3} spacing="4" skeletonHeight="3" />
            ) : machinesError ? (
              /* A seção não é uma tabela até haver dispositivos, então a falha é o
                 `<EmptyState tone="error">` — o par do `<TableErrorRow>` fora de tabela. */
              <EmptyState
                icon="lni lni-tab"
                tone="error"
                title="Não foi possível carregar os dispositivos"
                action={
                  <ActionButton action="refresh" label="Tentar novamente" showLabel mr={0} onClick={loadMachines} />
                }
              />
            ) : machines.length === 0 ? (
              <Flex justify="center" align="center" py={8} direction="column" gap={2}>
                <Text variant="caption">Nenhum dispositivo conectado</Text>
                <Text variant="caption">Clique em &ldquo;Ativar Novo Dispositivo&rdquo; para começar</Text>
              </Flex>
            ) : (
              <TableContainer>
                <Table>
                  <Thead>
                    <Tr>
                      <Th border="none" color="gray.400">
                        Dispositivo
                      </Th>
                      <Th border="none" color="gray.400">
                        Tipo
                      </Th>
                      <Th border="none" color="gray.400">
                        Status
                      </Th>
                      <Th border="none" color="gray.400">
                        <Tooltip label="Validade do token legacy (X-Machine-Token). O app renova a sessão automaticamente via POST /refresh enquanto o refresh_token estiver válido (90 dias para tablet).">
                          <Text as="span" cursor="help" borderBottom="1px dashed" borderColor="gray.400">
                            Sessão expira em
                          </Text>
                        </Tooltip>
                      </Th>
                      <Th border="none" />
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filteredDevices.length === 0 ? (
                      <TableEmptyRow colSpan={5} entity="dispositivo" />
                    ) : (
                      filteredDevices.map((machine) => (
                        <Tr key={machine.id} _hover={{ bg: 'gray.50' }}>
                          <Td border="none">
                            <Flex align="center" gap={3}>
                              <Flex
                                w="32px"
                                h="32px"
                                borderRadius="lg"
                                bg={machine.device_type === 'terminal' ? 'abraPurple.50' : 'blue.50'}
                                align="center"
                                justify="center"
                                flexShrink={0}
                              >
                                <Icon
                                  name={machine.device_type === 'terminal' ? 'lni lni-credit-cards' : 'lni lni-display'}
                                  size="md"
                                  color={machine.device_type === 'terminal' ? 'abraPurple.500' : 'blue.500'}
                                />
                              </Flex>
                              <Text fontSize="sm" fontWeight="500" color="gray.700">
                                {machine.name}
                              </Text>
                            </Flex>
                          </Td>
                          <Td border="none">
                            <Text variant="caption">
                              {machine.device_type === 'terminal' ? 'Maquininha' : 'Tablet'}
                            </Text>
                          </Td>
                          <Td border="none">
                            <Flex align="center" gap={1.5}>
                              <Box
                                w="6px"
                                h="6px"
                                borderRadius="full"
                                bg={isMachineActive(machine) ? 'green.400' : 'gray.300'}
                              />
                              <Text fontSize="xs" color={isMachineActive(machine) ? 'green.600' : 'gray.400'}>
                                {isMachineActive(machine) ? 'Ativo' : 'Inativo'}
                              </Text>
                            </Flex>
                          </Td>
                          <Td border="none">
                            <Text variant="caption">
                              {machine.session_expires_at ? formatDateTime(machine.session_expires_at) : '-'}
                            </Text>
                          </Td>
                          <Td border="none" textAlign="right">
                            {machine.status === 'active' && (
                              <Tooltip label="Desconectar dispositivo">
                                <IconButton
                                  aria-label="Desconectar"
                                  icon={<Icon name="lni lni-close" />}
                                  size="xs"
                                  variant="ghost"
                                  colorScheme="red"
                                  onClick={() => handleDeactivateClick(machine)}
                                />
                              </Tooltip>
                            )}
                          </Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </TableContainer>
            )}
          </SectionCard>
        </Collapse>
      )}

      {/* Auction config */}
      {hasContest && (
        <SectionCard title="Configurações do Leilão" mb={6}>
          <Flex gap={4} align="flex-end" flexWrap="wrap">
            <Box flex="1" minW="260px" maxW="300px">
              <FormField label="Encerramento do Leilão">
                <Input
                  type="datetime-local"
                  value={aedFormatted}
                  onChange={(e) => setAuctionEndsAt(e.target.value || null)}
                  size="sm"
                  borderRadius="lg"
                  disabled={savingAuctionConfig}
                />
              </FormField>
            </Box>
            <FormField label="Incremento de Lance" error={bidIncrementError} maxW="200px">
              <MoneyInput size="sm" value={bidIncrement} onChange={setBidIncrement} isDisabled={savingAuctionConfig} />
            </FormField>
            <Button
              colorScheme="magenta"
              size="sm"
              borderRadius="lg"
              onClick={handleSaveAuctionConfig}
              isLoading={savingAuctionConfig}
            >
              Salvar
            </Button>
          </Flex>
          <Box mt={4} pt={4} borderTop="1px" borderColor="gray.100">
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              {/* as="span" e não um <label>: um label de verdade alterna o
                  switch quando se clica no texto, e esta chave grava direto no
                  servidor — ninguém deve ligá-la sem ter mirado nela. O switch
                  carrega o nome por aria-label, então o leitor de tela continua
                  anunciando o mesmo. */}
              <FormLabel as="span" mb={0} fontSize="sm" color="gray.700">
                Avisar por WhatsApp
              </FormLabel>
              <ActionButton
                action="info"
                label="Ver quando e o que é enviado"
                size="xs"
                onClick={() => setWhatsappInfoOpen(true)}
              />
            </Box>
            <Switch
              id="auction_whatsapp_notifications"
              aria-label="Avisar por WhatsApp"
              isChecked={whatsappNotifications}
              onChange={(e) => handleToggleWhatsappNotifications(e.target.checked)}
              isDisabled={savingWhatsapp}
            />
            <Text variant="caption" mt={2}>
              Ligado, o visitante recebe um WhatsApp quando alguém supera o lance dele em uma obra e quando ele arremata
              alguma obra no encerramento do leilão. Esta chave é aplicada na hora.
            </Text>
          </Box>
        </SectionCard>
      )}

      {/* Stats row */}
      {liveLoading ? (
        <SimpleGrid columns={statColumns} spacing={4} mb={6}>
          {Array.from({ length: statCardCount }, (_, i) => (
            <Skeleton key={i} height="90px" borderRadius="xl" />
          ))}
        </SimpleGrid>
      ) : (
        <SimpleGrid columns={statColumns} spacing={4} mb={6}>
          <StatCard
            label="Total Check-ins"
            value={String(liveStats?.total_checkins || 0)}
            icon="lni lni-users"
            color="blue.500"
          />
          <StatCard
            label="Check-ins Ativos"
            value={String(liveStats?.active_checkins || 0)}
            icon="lni lni-checkmark-circle"
            color="abraGreen.500"
            helpText="Pessoas no evento agora"
          />
          <StatCard
            label="Infantis inscritos"
            value={String(liveStats?.child_participants || 0)}
            icon="lni lni-user"
            color="purple.500"
            helpText="Participantes com ingresso infantil"
          />
          <StatCard
            label="Infantis com check-in"
            value={String(liveStats?.child_checkins || 0)}
            icon="lni lni-checkmark"
            color="purple.500"
            helpText="Entrada sem cartão NFC"
          />
          {hasContest && (
            <>
              <StatCard
                label="Total de Lances"
                value={String(liveStats?.total_bids || 0)}
                icon="lni lni-hammer"
                color="abraPurple.500"
              />
              <StatCard
                label="Valor do Leilão"
                value={formatCurrency(liveStats?.total_auction_value ?? 0)}
                icon="lni lni-coin"
                color="magenta.500"
              />
            </>
          )}
        </SimpleGrid>
      )}

      {/* Main content grid */}
      {!liveLoading && (
        <Grid templateColumns={{ base: '1fr', lg: hasContest ? '1fr 1fr' : '1fr' }} gap={5} mb={6}>
          {/* Recent checkins */}
          <GridItem>
            <SectionCard
              title="Últimos Check-ins"
              h="100%"
              actions={
                liveStats && liveStats.active_checkins > 0 ? (
                  <Badge colorScheme="green">{liveStats.active_checkins} ativos</Badge>
                ) : undefined
              }
            >
              {liveError ? (
                <EmptyState
                  icon="lni lni-users"
                  tone="error"
                  title="Não foi possível carregar os check-ins"
                  action={
                    <ActionButton action="refresh" label="Tentar novamente" showLabel mr={0} onClick={loadLiveData} />
                  }
                />
              ) : recentCheckins.length === 0 ? (
                <EmptyState icon="lni lni-users" title="Nenhum check-in registrado" />
              ) : (
                <VStack spacing={0} align="stretch" divider={<Divider />}>
                  {recentCheckins.slice(0, 10).map((c) => (
                    <Flex key={c.id} justify="space-between" align="center" py={2.5}>
                      <Flex align="center" gap={3}>
                        <Avatar size="xs" name={c.participant_name} bg="blue.100" color="blue.600" />
                        <Box>
                          <Flex align="center" gap={1.5}>
                            <Text fontSize="sm" fontWeight="500" color="gray.700" lineHeight="1.2">
                              {c.participant_name}
                            </Text>
                            {c.is_child && <Badge colorScheme="purple">Infantil</Badge>}
                          </Flex>
                          <Text variant="caption" fontFamily="mono">
                            {c.is_child ? 'Sem cartão (NFC)' : c.tab_code}
                          </Text>
                        </Box>
                      </Flex>
                      <Flex direction="column" align="end">
                        <Text variant="caption">{formatDateTime(c.checkin_at)}</Text>
                        {c.checkout_at ? (
                          // Inclui check-in infantil convertido (entregou cartão) ou cancelado:
                          // ambas recebem checkout_at, então saem do "No evento".
                          <Text variant="caption">Saiu {formatDateTime(c.checkout_at)}</Text>
                        ) : (
                          <Badge colorScheme={c.is_child ? 'purple' : 'green'} mt={0.5}>
                            No evento
                          </Badge>
                        )}
                      </Flex>
                    </Flex>
                  ))}
                </VStack>
              )}
            </SectionCard>
          </GridItem>

          {/* Recent bids */}
          {hasContest && (
            <GridItem>
              <SectionCard
                title="Últimos Lances"
                h="100%"
                actions={
                  liveStats && liveStats.total_bids > 0 ? (
                    <Badge colorScheme="purple">{liveStats.total_bids} lances</Badge>
                  ) : undefined
                }
              >
                {liveError ? (
                  <EmptyState
                    icon="lni lni-hammer"
                    tone="error"
                    title="Não foi possível carregar os lances"
                    action={
                      <ActionButton action="refresh" label="Tentar novamente" showLabel mr={0} onClick={loadLiveData} />
                    }
                  />
                ) : recentBids.length === 0 ? (
                  <EmptyState icon="lni lni-hammer" title="Nenhum lance registrado" />
                ) : (
                  <VStack spacing={0} align="stretch" divider={<Divider />}>
                    {recentBids.slice(0, 10).map((b) => (
                      <Flex key={b.id} justify="space-between" align="center" py={2.5}>
                        <Flex align="center" gap={3} flex={1}>
                          <Avatar
                            size="xs"
                            name={b.participant_name || undefined}
                            bg={b.participant_name ? 'abraPurple.100' : 'gray.100'}
                            color={b.participant_name ? 'abraPurple.600' : 'gray.400'}
                          />
                          <Box flex={1}>
                            <Text fontSize="sm" fontWeight="500" color="gray.700" lineHeight="1.2">
                              {b.art_name}
                            </Text>
                            <Text variant="caption" fontFamily="mono">
                              {b.participant_name ? b.participant_name : '-'} • {b.tab_code}
                            </Text>
                          </Box>
                        </Flex>
                        <Flex direction="column" align="end">
                          <Text fontSize="sm" fontWeight="700" color="magenta.500" fontFamily="mono">
                            R$ {formatCurrencyValue(b.bid_amount)}
                          </Text>
                          <Text variant="caption">{formatDateTime(b.created_at)}</Text>
                        </Flex>
                      </Flex>
                    ))}
                  </VStack>
                )}
              </SectionCard>
            </GridItem>
          )}
        </Grid>
      )}

      {/* Artworks table */}
      {!liveLoading && hasContest && (
        <SectionCard
          title="Obras do Leilão"
          mb={6}
          actions={
            <Flex align="center" gap={3} flexWrap="wrap">
              {artworks.length > 0 && (
                <Input
                  size="sm"
                  placeholder="Buscar por obra ou autor"
                  value={artworkSearch}
                  onChange={(e) => setArtworkSearch(e.target.value)}
                  w="220px"
                  borderRadius="lg"
                  fontSize="xs"
                />
              )}
              {artworks.length > 0 && (
                <FormSelect
                  size="sm"
                  w="160px"
                  value={saleStatusFilter}
                  onChange={(value) => setSaleStatusFilter(value as typeof saleStatusFilter)}
                  options={[
                    { value: 'available', label: 'Em leilão' },
                    { value: 'sold', label: 'Vendida' },
                    { value: 'unsold', label: 'Não Vendida' }
                  ]}
                  placeholder="Todos os status"
                />
              )}
              {artworks.length > 0 && (
                <Button
                  size="sm"
                  colorScheme={auctionEnded ? 'green' : 'red'}
                  variant="solid"
                  onClick={() => setAuctionConfirmOpen(true)}
                  isLoading={auctionToggleLoading}
                  borderRadius="lg"
                >
                  {auctionEnded ? 'Reabrir Leilão' : 'Encerrar Leilão'}
                </Button>
              )}
              {artworks.length > 0 && (
                <ActionButton
                  action="download"
                  label="Exportar Repasse XLSX"
                  showLabel
                  mr={0}
                  isLoading={isExportingRepasse}
                  onClick={() =>
                    exportRepasse(
                      () => contestsService.downloadAuctionRepasseXlsx(event!.id),
                      'Erro ao exportar o repasse do leilão'
                    )
                  }
                />
              )}
            </Flex>
          }
        >
          {liveError ? (
            <EmptyState
              icon="lni lni-gallery"
              tone="error"
              title="Não foi possível carregar as obras do leilão"
              action={
                <ActionButton action="refresh" label="Tentar novamente" showLabel mr={0} onClick={loadLiveData} />
              }
            />
          ) : artworks.length === 0 ? (
            <EmptyState icon="lni lni-gallery" title="Nenhuma obra disponível para leilão" />
          ) : (
            <TableContainer>
              <Table>
                <Thead>
                  <Tr>
                    <Th border="none" color="gray.400">
                      Imagem
                    </Th>
                    <Th border="none" color="gray.400">
                      Obra
                    </Th>
                    <Th border="none" color="gray.400">
                      Autor
                    </Th>
                    <Th border="none" color="gray.400" isNumeric>
                      Lance Inicial
                    </Th>
                    <Th border="none" color="gray.400" isNumeric>
                      Lance Atual
                    </Th>
                    <Th border="none" color="gray.400">
                      Comprador
                    </Th>
                    <Th border="none" color="gray.400">
                      Status
                    </Th>
                    {auctionEnded && (
                      <Th border="none" color="gray.400">
                        Ações
                      </Th>
                    )}
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredArtworks.map((a, idx) => (
                    <Tr
                      key={a.id}
                      _hover={{ bg: 'gray.50' }}
                      bg={idx === 0 && a.current_bid ? 'magenta.50' : undefined}
                    >
                      <Td border="none">
                        <IconButton
                          aria-label="Ver imagem"
                          icon={
                            <Box
                              w="40px"
                              h="40px"
                              bg="gray.100"
                              borderRadius="md"
                              backgroundImage={a.art_image_url ? `url(${a.art_image_url})` : undefined}
                              backgroundSize="cover"
                              backgroundPosition="center"
                              cursor="pointer"
                              _hover={{ opacity: 0.8 }}
                            />
                          }
                          onClick={() => handleOpenPreview(a)}
                          variant="unstyled"
                          size="sm"
                        />
                      </Td>
                      <Td border="none">
                        <Flex align="center" gap={2}>
                          {idx === 0 && a.current_bid && <Text fontSize="xs">🔥</Text>}
                          <Text fontSize="sm" fontWeight="500" color="gray.700">
                            <ShortText text={a.name} beforeChars={20} afterChars={0} />
                          </Text>
                        </Flex>
                      </Td>
                      <Td border="none">
                        <Text variant="caption">
                          <ShortText text={a.author_name} beforeChars={20} afterChars={0} />
                        </Text>
                      </Td>
                      <Td border="none" isNumeric>
                        <Text variant="caption" fontFamily="mono">
                          {a.starting_bid ? `R$ ${formatCurrencyValue(a.starting_bid)}` : '-'}
                        </Text>
                      </Td>
                      <Td border="none" isNumeric>
                        {a.current_bid ? (
                          <Text fontSize="sm" fontWeight="700" color="magenta.500" fontFamily="mono">
                            R$ {formatCurrencyValue(a.current_bid)}
                          </Text>
                        ) : (
                          <Text fontSize="xs" color="gray.300">
                            Sem lance
                          </Text>
                        )}
                      </Td>
                      <Td border="none">
                        {a.current_bid_participant_name ? (
                          <Text fontSize="sm" fontWeight="500" color="gray.700">
                            {a.current_bid_participant_name ? a.current_bid_participant_name : '-'}
                          </Text>
                        ) : (
                          <Text fontSize="xs" color="gray.300">
                            -
                          </Text>
                        )}
                      </Td>
                      <Td border="none">
                        {(() => {
                          const status = getArtworkSaleStatus(a)
                          const isClickableForPostAuction =
                            a.sale_status === 'unsold' || (!a.available_for_auction && a.sale_status !== 'sold')
                          return isClickableForPostAuction ? (
                            <Tooltip label="Registrar venda pós-leilão">
                              <Button variant="unstyled" h="auto" minH={0} onClick={() => setPostAuctionArt(a)}>
                                <StatusBadge
                                  label={status.label}
                                  colorScheme={status.colorScheme}
                                  cursor="pointer"
                                  textDecoration="underline"
                                  textDecorationStyle="dashed"
                                  textUnderlineOffset="2px"
                                />
                              </Button>
                            </Tooltip>
                          ) : (
                            <StatusBadge label={status.label} colorScheme={status.colorScheme} />
                          )
                        })()}
                      </Td>
                      {auctionEnded && (
                        <Td border="none">
                          <RetiradaButton artwork={a} onClick={() => setPayoutModalArtId(a.id)} />
                        </Td>
                      )}
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          )}

          <ArtworkPreviewModal
            isOpen={isPreviewOpen}
            onClose={handleClosePreview}
            imageUrl={selectedArtwork?.art_image_url ?? null}
            artworkId={selectedArtwork?.id}
            onPrev={handlePrevArtwork}
            onNext={handleNextArtwork}
            positionLabel={
              selectedArtwork
                ? `${filteredArtworks.findIndex((a) => a.id === selectedArtwork.id) + 1} de ${filteredArtworks.length}`
                : undefined
            }
          />
        </SectionCard>
      )}

      {/* Inscritos */}
      <SectionCard
        title={<>Inscritos {!attendeesReplacing && <Badge colorScheme="blue">{attendeesTotal}</Badge>}</>}
        mb={6}
        actions={
          <Flex align="center" gap={2} flexWrap="wrap">
            {event?.tickets?.some((t) => t.type === 'FREE' && t.price > 0) && (
              <Button
                size="xs"
                colorScheme="orange"
                variant="outline"
                borderRadius="lg"
                onClick={handleOpenCaucaoModal}
                leftIcon={<Icon name="lni lni-shield" size="sm" />}
              >
                Cauções
              </Button>
            )}
            <Input
              size="sm"
              placeholder="Buscar por nome, e-mail ou telefone..."
              value={attendeesSearch}
              onChange={(e) => {
                setAttendeesSearch(e.target.value)
                setAttendeesPage(1)
              }}
              w={{ base: 'full', md: '320px' }}
              borderRadius="lg"
              fontSize="sm"
            />
            <Button
              size="sm"
              borderRadius="lg"
              variant={attendeesFilter === 'all' ? 'solid' : 'outline'}
              colorScheme={attendeesFilter === 'all' ? 'blue' : 'gray'}
              onClick={() => {
                setAttendeesFilter('all')
                setAttendeesPage(1)
              }}
            >
              Todos
            </Button>
            <Button
              size="sm"
              borderRadius="lg"
              variant={attendeesFilter === 'no_checkin' ? 'solid' : 'outline'}
              colorScheme={attendeesFilter === 'no_checkin' ? 'orange' : 'gray'}
              onClick={() => {
                setAttendeesFilter('no_checkin')
                setAttendeesPage(1)
              }}
            >
              Sem check-in
            </Button>
            <Button
              size="sm"
              borderRadius="lg"
              variant={attendeesFilter === 'no_checkout' ? 'solid' : 'outline'}
              colorScheme={attendeesFilter === 'no_checkout' ? 'red' : 'gray'}
              onClick={() => {
                setAttendeesFilter('no_checkout')
                setAttendeesPage(1)
              }}
            >
              Sem check-out
            </Button>
            <Tooltip label="Atualizar lista">
              <IconButton
                aria-label="Atualizar inscritos"
                icon={<Icon name="lni lni-reload" />}
                size="sm"
                variant="outline"
                borderRadius="lg"
                isLoading={attendeesLoading}
                onClick={() => loadAttendees(attendeesPage, attendeesSearch, attendeesFilter, true)}
              />
            </Tooltip>
          </Flex>
        }
      >
        {attendeesReplacing ? (
          <VStack spacing={2} align="stretch">
            {[1, 2, 3, 4, 5].map((i) => (
              <SkeletonText key={i} noOfLines={1} height="36px" borderRadius="md" />
            ))}
          </VStack>
        ) : attendeesError ? (
          <EmptyState
            icon="lni lni-users"
            tone="error"
            title="Não foi possível carregar os inscritos"
            action={
              <ActionButton
                action="refresh"
                label="Tentar novamente"
                showLabel
                mr={0}
                onClick={() => loadAttendees(attendeesPage, attendeesSearch, attendeesFilter)}
              />
            }
          />
        ) : attendees.length === 0 ? (
          <EmptyState icon="lni lni-users" title="Nenhum inscrito encontrado" />
        ) : (
          <TableContainer>
            <Table>
              <Thead>
                <Tr>
                  <Th border="none" color="gray.400">
                    Nome
                  </Th>
                  <Th border="none" color="gray.400">
                    E-mail
                  </Th>
                  <Th border="none" color="gray.400">
                    Telefone
                  </Th>
                  {showComanda && (
                    <Th border="none" color="gray.400">
                      Comanda
                    </Th>
                  )}
                  <Th border="none" color="gray.400">
                    Último Check-in
                  </Th>
                  <Th border="none" color="gray.400">
                    Último Check-out
                  </Th>
                  <Th border="none" color="gray.400">
                    Ingresso
                  </Th>
                  <Th border="none" color="gray.400">
                    Ações
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {attendees.map((a) => (
                  <Tr key={a.id} _hover={{ bg: 'gray.50' }}>
                    <Td border="none">
                      <Flex align="center" gap={2}>
                        <Text fontSize="sm" fontWeight="500" color="gray.700">
                          {a.name}
                        </Text>
                        {(a.is_child || a.is_child_ticket) && <Badge colorScheme="purple">Infantil</Badge>}
                        {a.is_door_sale && <Badge colorScheme="cyan">Porta</Badge>}
                      </Flex>
                    </Td>
                    <Td border="none">
                      <Text variant="caption">{a.email}</Text>
                    </Td>
                    <Td border="none">
                      <EmptyCell variant="caption" value={a.phone && formatPhone(a.phone)} />
                    </Td>
                    {showComanda && (
                      <Td border="none">
                        <Text variant="caption" fontFamily="mono">
                          {formatCurrency(a.comanda_value ?? 0)}
                        </Text>
                      </Td>
                    )}
                    <Td border="none">
                      {/* /attendees-checkin sends the raw wall-clock value, without a timezone */}
                      {a.last_checkin_at ? (
                        <Text variant="caption">{formatLocalDateTime(a.last_checkin_at)}</Text>
                      ) : (
                        <Badge colorScheme="orange">Sem check-in</Badge>
                      )}
                    </Td>
                    <Td border="none">
                      {a.last_checkout_at ? (
                        <Text variant="caption">{formatLocalDateTime(a.last_checkout_at)}</Text>
                      ) : a.last_checkin_at ? (
                        <Badge colorScheme="green">No evento</Badge>
                      ) : (
                        <Text fontSize="sm" color="gray.300">
                          —
                        </Text>
                      )}
                    </Td>
                    <Td border="none">
                      {a.ticket_type === 'FREE' && (a.ticket_price ?? 0) > 0 ? (
                        <Tooltip label={a.ticket_title ?? 'Caução reembolsável'}>
                          <Badge colorScheme="orange">Caução</Badge>
                        </Tooltip>
                      ) : (
                        <EmptyCell variant="caption" value={a.ticket_title} />
                      )}
                    </Td>
                    <Td border="none">
                      {(() => {
                        const action = getAvailableAction(a)
                        if (!action || !permissions.manageEventCheckin)
                          return (
                            <Text fontSize="sm" color="gray.300">
                              —
                            </Text>
                          )
                        return (
                          <Button
                            size="xs"
                            borderRadius="lg"
                            colorScheme={action === 'checkin' ? 'green' : 'orange'}
                            onClick={() =>
                              action === 'checkout' && !a.is_child && (a.comanda_value ?? 0) > 0
                                ? setPaymentCheckoutAttendee(a)
                                : setCheckinAction({ type: action, attendee: a })
                            }
                          >
                            {action === 'checkin' ? 'Check-in' : 'Check-out'}
                          </Button>
                        )
                      })()}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        )}

        {!attendeesReplacing && attendeesTotal > 20 && (
          <Flex justify="space-between" align="center" mt={4}>
            <Text variant="caption">
              Mostrando {Math.min((attendeesPage - 1) * 20 + 1, attendeesTotal)}–
              {Math.min(attendeesPage * 20, attendeesTotal)} de {attendeesTotal}
            </Text>
            <Flex gap={2}>
              <Button
                size="xs"
                variant="outline"
                borderRadius="lg"
                isDisabled={attendeesPage <= 1}
                onClick={() => {
                  const prev = attendeesPage - 1
                  setAttendeesPage(prev)
                  loadAttendees(prev, attendeesSearch, attendeesFilter)
                }}
              >
                Anterior
              </Button>
              <Button
                size="xs"
                variant="outline"
                borderRadius="lg"
                isDisabled={attendeesPage * 20 >= attendeesTotal}
                onClick={() => {
                  const next = attendeesPage + 1
                  setAttendeesPage(next)
                  loadAttendees(next, attendeesSearch, attendeesFilter)
                }}
              >
                Próxima
              </Button>
            </Flex>
          </Flex>
        )}
      </SectionCard>

      {/* WhatsApp Notifications Info Modal */}
      <AuctionWhatsAppInfoModal isOpen={whatsappInfoOpen} onClose={() => setWhatsappInfoOpen(false)} />

      {/* Caução Management Modal */}
      <FormModal
        isOpen={caucaoModalOpen}
        onClose={() => setCaucaoModalOpen(false)}
        title="Gerenciar Cauções"
        size="wide"
        footer={
          <>
            <Button size="sm" variant="ghost" onClick={() => setCaucaoModalOpen(false)}>
              Fechar
            </Button>
            {caucaoData && caucaoData.summary.refund_pending_count > 0 && (
              <Tooltip label="Consulta o Asaas e confirma os reembolsos já liquidados que ainda estão como 'Aguardando confirmação'.">
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="orange"
                  onClick={handleReconcileCaucao}
                  isLoading={reconcileLoading}
                  leftIcon={<Icon name="lni lni-reload" size="sm" />}
                >
                  Reverificar status ({caucaoData.summary.refund_pending_count})
                </Button>
              </Tooltip>
            )}
            {caucaoData && caucaoData.summary.eligible_count > 0 && (
              <Tooltip
                isDisabled={caucaoData.summary.refund_released}
                label={
                  caucaoData.summary.refund_release_at
                    ? `Disponível a partir de ${formatDateTime(
                        caucaoData.summary.refund_release_at
                      )} (1 dia após o encerramento do evento).`
                    : 'O reembolso das cauções só fica disponível 1 dia após o encerramento do evento.'
                }
              >
                {/* span wrapper so the tooltip still shows while the button is disabled */}
                <Box as="span" display="inline-block">
                  <Button
                    size="sm"
                    colorScheme="blue"
                    onClick={() => setBatchRefundConfirmOpen(true)}
                    isLoading={batchRefundLoading}
                    isDisabled={!caucaoData.summary.refund_released}
                    leftIcon={<Icon name="lni lni-reload" size="sm" />}
                  >
                    Reembolsar Elegíveis ({caucaoData.summary.eligible_count})
                  </Button>
                </Box>
              </Tooltip>
            )}
          </>
        }
      >
        {caucaoLoading && !caucaoData ? (
          <VStack spacing={3}>
            <Skeleton height="60px" width="100%" borderRadius="lg" />
            <Skeleton height="200px" width="100%" borderRadius="lg" />
          </VStack>
        ) : caucaoData && caucaoData.data.length > 0 ? (
          <>
            {/* Summary Cards */}
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={4}>
              <Box bg="gray.50" borderRadius="lg" p={3} textAlign="center">
                <Text variant="caption">Total</Text>
                <Text fontSize="lg" fontWeight="bold" color="gray.700">
                  {caucaoData.summary.total}
                </Text>
              </Box>
              <Box bg="green.50" borderRadius="lg" p={3} textAlign="center">
                <Text fontSize="xs" color="green.600" fontWeight="medium">
                  Elegíveis
                </Text>
                <Text fontSize="lg" fontWeight="bold" color="green.600">
                  {caucaoData.summary.eligible_count}
                </Text>
              </Box>
              <Box bg="orange.50" borderRadius="lg" p={3} textAlign="center">
                <Text fontSize="xs" color="orange.600" fontWeight="medium">
                  Aguardando
                </Text>
                <Text fontSize="lg" fontWeight="bold" color="orange.600">
                  {caucaoData.summary.refund_pending_count}
                </Text>
              </Box>
              <Box bg="blue.50" borderRadius="lg" p={3} textAlign="center">
                <Text fontSize="xs" color="blue.600" fontWeight="medium">
                  Reembolsados
                </Text>
                <Text fontSize="lg" fontWeight="bold" color="blue.600">
                  {caucaoData.summary.refunded_count}
                </Text>
              </Box>
            </SimpleGrid>

            {/* Refund window notice — caução refunds unlock 1 day after the event ends */}
            {!caucaoData.summary.refund_released && (
              <Alert status="info" borderRadius="lg" mb={4} fontSize="xs" alignItems="flex-start">
                <AlertIcon />
                <AlertDescription>
                  O reembolso das cauções só poderá ser solicitado a partir de{' '}
                  <Text as="span" fontWeight="bold">
                    {caucaoData.summary.refund_release_at
                      ? formatDateTime(caucaoData.summary.refund_release_at)
                      : '1 dia após o encerramento do evento'}
                  </Text>{' '}
                  (1 dia após o encerramento do evento).
                </AlertDescription>
              </Alert>
            )}

            {/* Participants Table */}
            <TableContainer>
              <Table>
                <Thead>
                  <Tr>
                    <Th color="gray.500">Nome</Th>
                    <Th color="gray.500">E-mail</Th>
                    <Th color="gray.500">Ingresso</Th>
                    <Th color="gray.500" isNumeric>
                      Valor
                    </Th>
                    <Th color="gray.500">Check-in</Th>
                    <Th color="gray.500">Status</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {caucaoData.data.map((p) => (
                    <Tr key={p.id}>
                      <Td border="none">
                        <Text fontSize="xs" fontWeight="medium" color="gray.700">
                          {p.name}
                        </Text>
                      </Td>
                      <Td border="none">
                        <Text variant="caption">{p.email}</Text>
                      </Td>
                      <Td border="none">
                        <EmptyCell variant="caption" value={p.ticket_title} />
                      </Td>
                      <Td border="none" isNumeric>
                        <Text variant="caption">R$ {formatCurrencyValue(p.paid_value)}</Text>
                      </Td>
                      <Td border="none">
                        <EmptyCell variant="caption" value={formatLocalDateTime(p.last_checkin_at)} />
                      </Td>
                      <Td border="none">
                        {p.payment_status === 'refunded' ? (
                          <Badge colorScheme="blue">Reembolsado</Badge>
                        ) : p.payment_status === 'refund_pending' ? (
                          <Badge colorScheme="orange">Aguardando confirmação</Badge>
                        ) : p.last_checkin_at ? (
                          <Badge colorScheme="green">Elegível</Badge>
                        ) : (
                          <Badge colorScheme="gray">Aguardando check-in</Badge>
                        )}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </>
        ) : caucaoError ? (
          <EmptyState
            icon="lni lni-coin"
            tone="error"
            title="Não foi possível carregar os participantes com caução"
            action={
              <ActionButton
                action="refresh"
                label="Tentar novamente"
                showLabel
                mr={0}
                onClick={loadCaucaoParticipants}
              />
            }
          />
        ) : (
          <Flex direction="column" align="center" py={8}>
            <Text variant="caption">Nenhum participante com caução encontrado neste evento.</Text>
          </Flex>
        )}
      </FormModal>

      {/* Batch Caução Refund Confirmation */}
      <ConfirmDialog
        isOpen={batchRefundConfirmOpen}
        onClose={() => setBatchRefundConfirmOpen(false)}
        onConfirm={handleBatchRefund}
        action="refund"
        title="Reembolsar cauções"
        description={`Deseja solicitar o reembolso da caução de todos os participantes elegíveis (${
          caucaoData?.summary.eligible_count ?? 0
        } participantes com check-in)? Esta ação não pode ser desfeita.`}
        confirmLabel="Confirmar reembolso"
        isLoading={batchRefundLoading}
      />

      {/* Device Name Modal */}
      <FormModal
        isOpen={nameModalOpen}
        onClose={() => setNameModalOpen(false)}
        title="Nome do Dispositivo"
        size="compact"
        footer={
          <>
            <Button variant="ghost" size="sm" mr={2} onClick={() => setNameModalOpen(false)}>
              Cancelar
            </Button>
            <Button
              colorScheme="magenta"
              size="sm"
              onClick={handleGenerateCode}
              isDisabled={!deviceName.trim()}
              isLoading={generatingCode}
            >
              Gerar Código
            </Button>
          </>
        }
      >
        <FormField label="Insira um nome para identificar o dispositivo" error={generateCodeError}>
          <Input
            placeholder="Ex: Tablet Galeria 1"
            value={deviceName}
            onChange={(e) => {
              setDeviceName(e.target.value)
              if (generateCodeError) setGenerateCodeError(null)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && deviceName.trim()) handleGenerateCode()
            }}
            autoFocus
          />
        </FormField>
      </FormModal>

      {/* Activation Code Modal */}
      <FormModal isOpen={qrModalOpen} onClose={handleCloseQrModal} title="Ativar Dispositivo" size="compact">
        <Alert status="info" borderRadius="lg" mb={4} fontSize="xs" textAlign="left">
          <AlertIcon boxSize={4} />
          <AlertDescription>
            Este código autentica <b>apenas tablets de leilão</b>. Maquininhas de check-in/check-out devem ser
            cadastradas no módulo de Maquininhas.
          </AlertDescription>
        </Alert>
        <Text variant="caption" mb={3}>
          Digite este código no dispositivo para ativá-lo
        </Text>
        <Box bg="gray.50" borderRadius="xl" border="1px" borderColor="gray.200" py={5} px={4} mb={4}>
          <Text fontFamily="mono" fontSize="5xl" fontWeight="800" letterSpacing="0.3em" color="gray.800" lineHeight="1">
            {activationCode}
          </Text>
        </Box>
        <SectionCard variant="tile" display="inline-block" mb={3}>
          <QRCodeCanvas value={activationCode} size={140} includeMargin />
        </SectionCard>
        <Text variant="caption" mb={1}>
          Dispositivos com câmera podem ler o QR Code
        </Text>
        <Text variant="caption" mt={2}>
          Código expira em: {activationExpiresAt ? formatDateTime(activationExpiresAt) : ''}
        </Text>
      </FormModal>

      {/* Deactivate confirmation */}
      <ConfirmDialog
        isOpen={confirmDeactivateOpen}
        onClose={() => {
          setConfirmDeactivateOpen(false)
          setDeactivateTarget(null)
        }}
        onConfirm={handleConfirmDeactivate}
        action="lock"
        title="Desconectar dispositivo"
        description={`Deseja realmente desconectar ${deactivateTarget?.name}? O dispositivo será desativado e precisará ser pareado de novo.`}
        confirmLabel="Desconectar"
      />

      {/* Manual check-in / check-out confirmation */}
      <ConfirmDialog
        isOpen={checkinAction !== null}
        onClose={() => {
          if (!checkinActionLoading) setCheckinAction(null)
        }}
        onConfirm={handleCheckinAction}
        action={checkinAction?.type === 'checkin' ? 'approve' : 'refund'}
        title={checkinAction?.type === 'checkin' ? 'Confirmar check-in' : 'Confirmar check-out'}
        description={
          checkinAction?.type === 'checkin'
            ? `Deseja realizar o check-in de ${checkinAction?.attendee.name}?`
            : `Deseja realizar o check-out de ${checkinAction?.attendee.name}?`
        }
        confirmLabel="Confirmar"
        isLoading={checkinActionLoading}
      />

      {/* Toggle auction confirmation */}
      <ConfirmDialog
        isOpen={auctionConfirmOpen}
        onClose={() => setAuctionConfirmOpen(false)}
        onConfirm={handleToggleAuction}
        action={auctionEnded ? 'unlock' : 'lock'}
        title={auctionEnded ? 'Reabrir leilão' : 'Encerrar leilão'}
        description={
          auctionEnded
            ? 'Os tablets voltam a registrar novos lances neste evento.'
            : 'O leilão será encerrado em 1 minuto e os tablets param de registrar lances.'
        }
        confirmLabel={auctionEnded ? 'Reabrir leilão' : 'Encerrar leilão'}
        isLoading={auctionToggleLoading}
      />

      {/* Payout / Retirada modal */}
      {payoutModalArtId !== null && (
        <PayoutModal
          isOpen
          onClose={() => setPayoutModalArtId(null)}
          artId={payoutModalArtId}
          onPaymentMarked={loadLiveData}
        />
      )}

      {/* Check-out with payment (ABRA-425) */}
      <CheckoutPaymentModal
        isOpen={paymentCheckoutAttendee !== null}
        onClose={() => setPaymentCheckoutAttendee(null)}
        eventId={event.id}
        attendee={paymentCheckoutAttendee}
        onDone={() => loadAttendees(attendeesPage, attendeesSearch, attendeesFilter, true)}
      />

      {/* Post-auction sale modal */}
      {postAuctionArt !== null && (
        <PostAuctionSaleModal
          isOpen
          onClose={() => setPostAuctionArt(null)}
          artwork={postAuctionArt}
          onSaleRegistered={loadLiveData}
        />
      )}
    </>
  )
}

/* ─── Sub-components ─────────────────────────────── */

function RetiradaButton({ artwork, onClick }: { artwork: LiveArtwork; onClick: () => void }) {
  const isPaid = !!artwork.paid_to_applicant_at
  const isSold = artwork.sale_status === 'sold'

  let colorScheme = 'gray'
  let tooltipLabel = 'Ver dados de retirada'
  let iconClass = 'lni lni-package'

  if (isPaid) {
    colorScheme = 'green'
    tooltipLabel = 'Pago — ver dados de retirada'
    iconClass = 'lni lni-checkmark-circle'
  } else if (isSold) {
    colorScheme = 'yellow'
    tooltipLabel = 'Vendida — pagamento ao aluno pendente'
    iconClass = 'lni lni-coin'
  }

  return (
    <Tooltip label={tooltipLabel}>
      <Button
        size="xs"
        colorScheme={colorScheme}
        variant={isPaid ? 'solid' : 'outline'}
        onClick={onClick}
        leftIcon={<i className={iconClass} style={{ fontSize: 11 }} />}
      >
        Retirada
      </Button>
    </Tooltip>
  )
}
